import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import type { NodoDoc } from "@/componentes/documentos/Documento"
import { EditorDocumento } from "@/componentes/documentos/EditorDocumento"
import { Boton } from "@/componentes/ui/Boton"
import { Cajon } from "@/componentes/ui/Cajon"

import { EditorCampos } from "./EditorCampos"
import { CATEGORIAS, type CategoriaPlantilla, type PlantillaTH, type UsoPlantilla } from "./tipos"

type Variable = { clave: string; descripcion: string }

const VACIA: Omit<PlantillaTH, "id" | "version"> = {
  codigo: "",
  nombre: "",
  descripcion: null,
  categoria: "SOLICITUD",
  uso: "EMPLEADO",
  cuerpo: { type: "doc", content: [{ type: "paragraph" }] },
  campos: [],
  requiere_aprobacion: true,
  activa: true,
}

/**
 * Redactar una plantilla: el texto con sus `{variables}` y los datos que pide.
 *
 * ⚠ **Cambiar el texto o los campos sube la VERSIÓN** (lo hace el servidor), y los
 * documentos ya emitidos no cambian: congelaron el suyo.
 *
 * ⚠ **La lista de variables de la ficha la da el SERVIDOR** (`/plantillas-th/variables`),
 * que es quien las rellena: escribirla aquí sería una segunda lista que se queda vieja.
 */
export function EditorPlantillaTH({
  plantilla,
  onCerrar,
}: {
  plantilla: PlantillaTH | null
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [datos, setDatos] = useState(plantilla ?? VACIA)
  const poner = (cambio: Partial<typeof datos>) => setDatos((d) => ({ ...d, ...cambio }))

  const variables = useQuery({
    queryKey: ["plantillas-th", "variables"],
    queryFn: () => api.get<Variable[]>("/api/v1/plantillas-th/variables"),
    staleTime: 10 * 60_000,
  })

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        codigo: datos.codigo,
        nombre: datos.nombre,
        descripcion: datos.descripcion?.trim() || null,
        categoria: datos.categoria,
        uso: datos.uso,
        cuerpo: datos.cuerpo,
        campos: datos.campos,
        requiere_aprobacion: datos.requiere_aprobacion,
      }
      return plantilla
        ? api.patch<PlantillaTH>(`/api/v1/plantillas-th/${plantilla.id}`, {
            ...cuerpo,
            activa: datos.activa,
          })
        : api.post<PlantillaTH>("/api/v1/plantillas-th", cuerpo)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["plantillas-th"] })
      onCerrar()
    },
    meta: { exito: plantilla ? "Plantilla guardada" : "Plantilla creada" },
  })

  return (
    <Cajon
      abierto={true}
      titulo={plantilla ? `${plantilla.nombre} · v${plantilla.version}` : "Nueva plantilla"}
      onCerrar={onCerrar}
      ancho="amplio"
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
        <div>
          <label htmlFor="pl-codigo" className="mb-1 block text-xs font-medium uppercase">
            Código
          </label>
          <input
            id="pl-codigo"
            className="campo font-mono"
            value={datos.codigo}
            onChange={(e) => poner({ codigo: e.target.value.toUpperCase() })}
          />
        </div>
        <div>
          <label htmlFor="pl-nombre" className="mb-1 block text-xs font-medium uppercase">
            Nombre
          </label>
          <input
            id="pl-nombre"
            className="campo"
            value={datos.nombre}
            onChange={(e) => poner({ nombre: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pl-desc" className="mb-1 block text-xs font-medium uppercase">
            Para qué sirve
          </label>
          <input
            id="pl-desc"
            className="campo"
            value={datos.descripcion ?? ""}
            onChange={(e) => poner({ descripcion: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="pl-cat" className="mb-1 block text-xs font-medium uppercase">
            Categoría
          </label>
          <select
            id="pl-cat"
            className="campo"
            value={datos.categoria}
            onChange={(e) => poner({ categoria: e.target.value as CategoriaPlantilla })}
          >
            {CATEGORIAS.map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.etiqueta}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="pl-uso" className="mb-1 block text-xs font-medium uppercase">
            Quién lo genera
          </label>
          <select
            id="pl-uso"
            className="campo"
            value={datos.uso}
            onChange={(e) => poner({ uso: e.target.value as UsoPlantilla })}
          >
            <option value="EMPLEADO">El empleado, con sus datos (solicitud)</option>
            <option value="TALENTO_HUMANO">Talento Humano, eligiendo al empleado</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-4 sm:col-span-2">
          {datos.uso === "EMPLEADO" && (
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={datos.requiere_aprobacion}
                onChange={(e) => poner({ requiere_aprobacion: e.target.checked })}
              />
              Talento Humano la aprueba antes de valer
            </label>
          )}
          {plantilla && (
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={datos.activa}
                onChange={(e) => poner({ activa: e.target.checked })}
              />
              Activa (se ofrece al generar)
            </label>
          )}
        </div>
      </div>

      <h3 className="mt-5 mb-2 text-sm font-semibold">Texto</h3>
      <EditorDocumento valor={datos.cuerpo} alCambiar={(cuerpo: NodoDoc) => poner({ cuerpo })} />
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer" style={{ color: "var(--fg-muted)" }}>
          Datos de la ficha que se pueden escribir entre llaves
        </summary>
        <ul className="mt-2 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
          {(variables.data ?? []).map((v) => (
            <li key={v.clave}>
              <code>{`{${v.clave}}`}</code>{" "}
              <span style={{ color: "var(--fg-muted)" }}>{v.descripcion}</span>
            </li>
          ))}
        </ul>
      </details>

      <h3 className="mt-5 mb-2 text-sm font-semibold">Datos que pide al generarse</h3>
      <EditorCampos campos={datos.campos} onCambiar={(campos) => poner({ campos })} />

      {guardar.error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton
          variante="primario"
          disabled={guardar.isPending || datos.codigo.length < 3 || datos.nombre.length < 3}
          onClick={() => guardar.mutate()}
        >
          Guardar
        </Boton>
      </div>
    </Cajon>
  )
}
